import { AudioEngine } from './AudioEngine';

/**
 * Thin wrapper around the CrazyGames SDK v3, with safe no-op fallbacks so the game runs identically
 * outside their portal (local dev / our own hosting). Loaded via the script tag in index.html.
 *
 * Responsibilities: init, gameplay/loading signals (so they gate ads correctly), happytime on wins,
 * and midgame / rewarded ads. Game audio is muted for the ad's duration (a portal requirement).
 */
type AdType = 'midgame' | 'rewarded';
interface AdCallbacks { adStarted?: () => void; adFinished?: () => void; adError?: (e?: unknown) => void }
interface CrazyGamesSDK {
  init(): Promise<void>;
  environment?: string;
  game: {
    gameplayStart(): void;
    gameplayStop(): void;
    happytime(): void;
    loadingStart?(): void;
    loadingStop?(): void;
    sdkGameLoadingStop?(): void;
  };
  ad: { requestAd(type: AdType, callbacks: AdCallbacks): void };
  data?: {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
    clear(): void;
  };
}
function sdk(): CrazyGamesSDK | null {
  return (window as unknown as { CrazyGames?: { SDK?: CrazyGamesSDK } }).CrazyGames?.SDK ?? null;
}

export class CrazyGames {
  private static ready = false;

  /** True only once init() succeeded on the actual portal SDK. */
  static get ready_(): boolean { return this.ready; }

  static async init(): Promise<void> {
    // Not iframed → we're on our own hosting (top window), not a portal. Skip entirely so the boot
    // never waits on the CrazyGames SDK handshake (which never completes off-portal → blue screen).
    try { if (window.self === window.top) return; } catch { /* cross-origin top access throws → we ARE iframed, continue */ }
    // SDK script is loaded async; wait briefly for it to appear.
    const s = await CrazyGames.waitForSdk(1500);
    if (!s) return;
    try {
      // Time-box init() so a non-CrazyGames iframe (e.g. itch.io) can't hang the boot indefinitely.
      await Promise.race([
        s.init(),
        new Promise<void>((_, reject) => setTimeout(() => reject(new Error('cg-init-timeout')), 2500)),
      ]);
      this.ready = true;
    } catch { /* timed out / failed off-portal — fall back to our own persistence */ }
  }

  /** Poll for the async-loaded SDK script up to `timeoutMs`, resolving null if it never appears. */
  private static waitForSdk(timeoutMs: number): Promise<CrazyGamesSDK | null> {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = (): void => {
        const s = sdk();
        if (s) { resolve(s); return; }
        if (Date.now() - start > timeoutMs) { resolve(null); return; }
        setTimeout(tick, 100);
      };
      tick();
    });
  }

  /** Game finished loading and is interactive (call after Preload). */
  static loadingStop(): void { try { sdk()?.game.loadingStop?.(); sdk()?.game.sdkGameLoadingStop?.(); } catch { /* ignore */ } }

  // ── Data Module: persistent key-value storage on the portal (synced to the CrazyGames user when
  // signed in). Available only after init() succeeds on the actual SDK; the game falls back to its
  // own IndexedDB/localStorage off-portal.
  static get hasData(): boolean { return !!sdk()?.data; }
  static dataGet(key: string): string | null { try { return sdk()?.data?.getItem(key) ?? null; } catch { return null; } }
  static dataSet(key: string, value: string): void { try { sdk()?.data?.setItem(key, value); } catch { /* ignore */ } }

  /** Wrap ACTUAL gameplay (a race). The portal won't show ads during gameplay. */
  static gameplayStart(): void { try { sdk()?.game.gameplayStart(); } catch { /* ignore */ } }
  static gameplayStop(): void { try { sdk()?.game.gameplayStop(); } catch { /* ignore */ } }
  /** Signal a moment of joy (win / record) — helps their ad timing. */
  static happytime(): void { try { sdk()?.game.happytime(); } catch { /* ignore */ } }

  private static muteForAd(on: boolean, prev?: boolean): boolean {
    const wasMuted = prev ?? AudioEngine.muted;
    AudioEngine.setMuted(on ? true : wasMuted);
    return wasMuted;
  }

  /** Rewarded ad: `onReward` fires ONLY if the ad completed. `onClose` always fires at the end. */
  static rewarded(onReward: () => void, onClose?: () => void): void {
    const s = sdk();
    if (!s) { onClose?.(); return; } // no SDK (e.g. our own hosting) → nothing to show
    let wasMuted = false;
    try {
      s.ad.requestAd('rewarded', {
        adStarted: () => { wasMuted = this.muteForAd(true); },
        adFinished: () => { this.muteForAd(false, wasMuted); onReward(); onClose?.(); },
        adError: () => { this.muteForAd(false, wasMuted); onClose?.(); },
      });
    } catch { onClose?.(); }
  }

  /** Midgame (interstitial) ad at a natural break. The portal frequency-caps it. */
  static midgame(onDone?: () => void): void {
    const s = sdk();
    if (!s) { onDone?.(); return; }
    let wasMuted = false;
    try {
      s.ad.requestAd('midgame', {
        adStarted: () => { wasMuted = this.muteForAd(true); },
        adFinished: () => { this.muteForAd(false, wasMuted); onDone?.(); },
        adError: () => { this.muteForAd(false, wasMuted); onDone?.(); },
      });
    } catch { onDone?.(); }
  }
}
